/**
 * Hooks do M3 TARP (GUPM-169) — server state via TanStack Query + realtime.
 * A UI só fala com estes hooks; nunca com Supabase direto (regra #2).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { asDamId } from '@/domain';
import { useAuth } from '@/hooks/useAuth';
import { pollingQueryOptions } from '@/lib/query-polling';
import { OPEN_STATUSES } from '@/domain/tarp/status';
import {
  useDataSources,
  type CloseTarpInput,
  type DraftPatch,
  type OpenTarpInput,
  type TarpRecord,
} from '@/services/datasources';
import { subscribeTarpCritical } from '@/services/datasources/tarp-realtime';

const TARPS_ROOT = ['tarps'] as const;

/**
 * Planos de ação já vinculados a uma TARP (GUPM-213 Extra 2) — alimenta a opção
 * "Vincular plano existente" no Bloco 3 da baixa. Só busca quando há uma TARP.
 */
export function usePlanosByTarp(tarpRecordId: string | null | undefined) {
  const { planos } = useDataSources();
  return useQuery({
    queryKey: ['planos', 'by-tarp', tarpRecordId ?? 'none'],
    queryFn: () => planos.listPlanosByTarp(tarpRecordId as string),
    enabled: !!tarpRecordId,
    staleTime: 30 * 1000,
  });
}

/**
 * Lista os TARPs em aberto. Atualiza sozinha por dois caminhos: PUSH (broadcast
 * `tarp:critical`, inclui o `refresh` do rascunho — outro usuário vê a mudança
 * na hora) + um POLLING de rede de segurança (escrita fora do app ou broadcast
 * perdido converge em ~30s, mesmo intervalo do dashboard).
 */
export function useOpenTarps() {
  const { tarp } = useDataSources();
  const qc = useQueryClient();

  useEffect(() => {
    const unsub = subscribeTarpCritical(() => {
      void qc.invalidateQueries({ queryKey: TARPS_ROOT });
    });
    return unsub;
  }, [qc]);

  return useQuery({
    queryKey: [...TARPS_ROOT, 'open'],
    queryFn: () => tarp.listTarps({ status: [...OPEN_STATUSES] }),
    ...pollingQueryOptions, // rede de segurança do push
  });
}

/**
 * TARPs ENCERRADAS (GUPM-188) — alimenta o painel de fechadas (auditoria) no rodapé
 * do M3. Mesma queryKey do `useDamScope` (`['tarps','encerradas']`) → compartilha o
 * fetch. A subscription do `useOpenTarps` (sempre montado no M3) invalida o root
 * `['tarps']`, cobrindo esta query também.
 */
export function useClosedTarps() {
  const { tarp } = useDataSources();
  return useQuery({
    queryKey: [...TARPS_ROOT, 'encerradas'],
    queryFn: () => tarp.listBaixasEncerradas(),
    ...pollingQueryOptions,
  });
}

/**
 * Autoria das TARPs da lista (GUPM-292) — quem iniciou, editou, abriu e deu baixa.
 *
 * UMA consulta para a lista inteira (a função no banco recebe `uuid[]`), não uma
 * por card. A chave inclui os ids ordenados para o cache não confundir listas
 * diferentes nem refazer a busca quando só a ORDEM da lista muda.
 *
 * Compartilha o root `['tarps']`, então o broadcast/polling que atualiza a lista
 * também revalida a autoria — quem entra editando aparece sem reload.
 */
export function useTarpAutoria(tarpIds: string[]) {
  const { tarp } = useDataSources();
  const chave = [...tarpIds].sort();
  return useQuery({
    queryKey: [...TARPS_ROOT, 'autoria', chave],
    queryFn: () => tarp.listAutoria(chave),
    enabled: chave.length > 0,
    staleTime: 30 * 1000,
  });
}

export function useActiveDams() {
  const { estrutura } = useDataSources();
  return useQuery({
    queryKey: ['dams', 'active'],
    queryFn: () => estrutura.listActiveDams(),
  });
}

export function useInstrumentsForDam(damId: string | null) {
  const { instrumentacao } = useDataSources();
  return useQuery({
    queryKey: ['instruments', 'dam', damId],
    queryFn: () => instrumentacao.listInstruments({ damId: asDamId(damId ?? '') }),
    enabled: !!damId,
  });
}

/**
 * Usuário do app autenticado (id = app_users.id) — p/ checar autoria do rascunho.
 *
 * GUPM-241: a chave INCLUI o id da sessão. Antes era `['current-app-user']` fixa
 * com `staleTime` de 5 min; como o logout é navegação SPA (sem reload), quem
 * entrasse depois de outro na mesma aba recebia a identidade do anterior — e o
 * `canDiscard` do M3 escondia "Descartar rascunho" do próprio autor. Medido:
 * sessão `admin@`, identidade servida `vigilante2@` (`operador`), cache 259 s.
 * Com o id na chave isso é impossível: outro usuário, outra entrada de cache.
 *
 * `enabled` evita buscar sem sessão (e, com a chave por usuário, garante que
 * "sem sessão" nunca reaproveite a identidade de ninguém).
 */
export function useCurrentAppUser() {
  const { shiftHandover } = useDataSources();
  const { user } = useAuth();
  return useQuery({
    queryKey: ['current-app-user', user?.id ?? 'anon'],
    queryFn: () => shiftHandover.getCurrentUser(),
    enabled: !!user?.id,
    staleTime: 5 * 60 * 1000,
  });
}

const AUTOSAVE_MS = 600;

/** Conteúdo do rascunho que já está no banco — baseline da guarda do GUPM-293. */
function baselineDoRegistro(rec: TarpRecord | null | undefined): DraftPatch {
  return {
    flowState: rec?.flowState ?? undefined,
    observation: rec?.observation ?? undefined,
    stepsCompleted: rec?.stepsCompleted ?? undefined,
  };
}

/**
 * O patch não muda NADA do que já está persistido?
 *
 * Campo ausente no patch significa "não mexer" — não entra na comparação, senão um
 * patch parcial pareceria sempre diferente do baseline. A normalização importa: o
 * `textarea` de observação devolve `''` onde o banco tem `null`, e tratar isso como
 * diferença gravaria a cada abertura de rascunho (era o caso real medido).
 */
function patchSemMudanca(patch: DraftPatch, base: DraftPatch): boolean {
  const path = (s: DraftPatch['flowState']) => JSON.stringify(s?.path ?? null);
  if (patch.flowState !== undefined && path(patch.flowState) !== path(base.flowState)) return false;
  if (patch.observation !== undefined && patch.observation !== (base.observation ?? '')) return false;
  if (patch.stepsCompleted !== undefined && patch.stepsCompleted !== (base.stepsCompleted ?? 0)) {
    return false;
  }
  return true;
}

/**
 * Rascunho "Em abertura" (GUPM-178 Fase B) — cria/autosalva/confirma/descarta.
 * - `scheduleSave` é debounced; a 1ª chamada cria o rascunho (EM_ABERTURA) com o
 *   conteúdo como estado inicial, as seguintes autosalvam. Guarda contra corrida
 *   na criação (uma promessa de create compartilhada).
 * - `confirm` faz flush + confirma a abertura (→ ABERTA) e devolve o id.
 * - `discard` faz soft-delete (RLS/trigger garantem: só o autor, só EM_ABERTURA).
 */
export function useTarpDraft(existing?: TarpRecord | null) {
  const { tarp } = useDataSources();
  const qc = useQueryClient();
  const invalidate = useCallback(
    () => void qc.invalidateQueries({ queryKey: TARPS_ROOT }),
    [qc],
  );

  const draftIdRef = useRef<string | null>(existing?.id ?? null);
  // GUPM-293: conteúdo já gravado. Avança a cada gravação bem-sucedida.
  const baseline = useRef<DraftPatch>(baselineDoRegistro(existing));
  const createRef = useRef<Promise<string> | null>(null);
  const latest = useRef<{ input: OpenTarpInput; patch: DraftPatch } | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // GUPM-298: "esta pessoa alterou algo nesta visita" — pergunta DIFERENTE de
  // `dirty` ("tem gravação em voo"). `dirty` vive o debounce de 600 ms e voltar a
  // false depois de gravar está certo; amarrar rodapé e confirmação de saída a ele
  // fazia o botão trocar de rótulo debaixo do cursor. Liga na primeira alteração
  // real (a que passa pela guarda de conteúdo do GUPM-293) e só desliga ao descartar.
  const [alterouNestaVisita, setAlterouNestaVisita] = useState(false);
  const [draftId, setDraftId] = useState<string | null>(existing?.id ?? null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  // GUPM-262: há alteração agendada e ainda não gravada? Decide se fechar o
  // modal precisa de flush/confirmação (o debounce de 600 ms não pode virar
  // perda silenciosa ao sair).
  const [dirty, setDirty] = useState(false);

  const doSave = useCallback(async () => {
    const l = latest.current;
    if (!l) return;
    latest.current = null;
    setSaving(true);
    try {
      if (!draftIdRef.current && !createRef.current) {
        // Primeira gravação: cria o rascunho já com o conteúdo inicial.
        createRef.current = tarp.openDraft(l.input, l.patch).then((rec) => {
          draftIdRef.current = rec.id;
          setDraftId(rec.id);
          return rec.id;
        });
        await createRef.current;
        invalidate();
      } else {
        const id = draftIdRef.current ?? (await createRef.current!);
        await tarp.saveDraft(id, l.patch);
      }
      // GUPM-293: o que acabou de ser gravado passa a ser o baseline — senão
      // reportar de novo o mesmo conteúdo (a hidratação após um autosave) gravaria
      // outra vez.
      baseline.current = { ...baseline.current, ...l.patch };
      setSaveError(null);
      // Um scheduleSave pode ter chegado DURANTE o await — só limpa se não há
      // pendência nova.
      setDirty(latest.current !== null);
    } catch (e) {
      // Falha do autosave NÃO pode ser silenciosa (mobile/4G — regra #10).
      // Reseta a promessa de criação para permitir retry (senão uma falha
      // transitória na criação travaria o autosave da sessão inteira).
      if (!draftIdRef.current) createRef.current = null;
      // Restaura o payload que falhou (se nada mais novo chegou durante o
      // await) — senão o próximo flush ("Manter rascunho") viraria no-op de
      // sucesso e fecharia sem persistir (CRITICAL do review do PR #152).
      if (!latest.current) latest.current = l;
      setSaveError(e instanceof Error ? e.message : String(e));
      throw e;
    } finally {
      setSaving(false);
    }
  }, [tarp, invalidate]);

  const scheduleSave = useCallback(
    (input: OpenTarpInput, patch: DraftPatch) => {
      // GUPM-293: abrir um rascunho não é editá-lo. Se o formulário devolve o que já
      // está no banco — a hidratação ao abrir, ou uma edição desfeita —, não há o que
      // gravar: o UPDATE não mudaria nada e ainda assim viraria linha de `audit_log`,
      // creditando "editou" a quem só leu.
      //
      // A guarda é por CONTEÚDO, não por ciclo de vida. Travar o efeito de hidratação
      // (`mounted` ref no TarpFlowRunner) não resolve: o StrictMode a derrota em dev e
      // qualquer remount futuro a derrotaria em produção.
      //
      // Só vale com rascunho JÁ persistido: sem id não há baseline, e o primeiro
      // report (barragem escolhida, ainda sem checklist) é justamente o que CRIA o
      // rascunho — bloqueá-lo impediria a criação.
      if (draftIdRef.current && patchSemMudanca(patch, baseline.current)) {
        if (timer.current) {
          clearTimeout(timer.current);
          timer.current = null;
        }
        latest.current = null;
        setDirty(false);
        return;
      }
      latest.current = { input, patch };
      setDirty(true);
      setAlterouNestaVisita(true);
      if (timer.current) clearTimeout(timer.current);
      // O erro é exposto via `saveError`; aqui só evitamos unhandled rejection.
      timer.current = setTimeout(() => {
        void doSave().catch(() => {});
      }, AUTOSAVE_MS);
    },
    [doSave],
  );

  const flush = useCallback(async () => {
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }
    await doSave();
    // GUPM-262: o `doSave` só invalida no ramo de CRIAÇÃO, então sair de um
    // rascunho já existente por "Manter rascunho" gravava e devolvia a lista com
    // o progresso velho (até o polling de 30 s). `flush` só vem de ação explícita
    // — "Manter rascunho" e `confirm` —, nunca do debounce, logo sincronizar aqui
    // não recria o storm que o GUPM-178 evitou ao silenciar o autosave. Fica
    // depois do await: flush que falha não mexeu no banco, não há o que ressincronizar.
    invalidate();
  }, [doSave, invalidate]);

  const confirm = useCallback(async (): Promise<string | null> => {
    await flush(); // grava o pendente (cria o rascunho se preciso); propaga erro
    // Aguarda um create em voo antes de ler o id (evita confirmar "no vazio"
    // numa corrida entre o timer de autosave e o clique em Abrir).
    let id = draftIdRef.current;
    if (!id && createRef.current) id = await createRef.current.catch(() => null);
    if (!id) return null;
    await tarp.confirmAbertura(id);
    invalidate();
    return id;
  }, [flush, tarp, invalidate]);

  const discard = useCallback(async () => {
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }
    latest.current = null;
    setDirty(false);
    // Descartar encerra a visita: não há mais alteração a manter.
    setAlterouNestaVisita(false);
    const id = draftIdRef.current;
    if (!id) return;
    await tarp.discardDraft(id);
    invalidate();
  }, [tarp, invalidate]);

  // Descarta o timer pendente no unmount (evita autosave após fechar o modal).
  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    [],
  );

  return {
    draftId,
    dirty,
    alterouNestaVisita,
    saving,
    saveError,
    scheduleSave,
    flush,
    confirm,
    discard,
  };
}

/** Mutações do ciclo de vida do TARP. Cada uma invalida a lista de abertos. */
export function useTarpMutations() {
  const { tarp } = useDataSources();
  const qc = useQueryClient();
  const invalidate = () => qc.invalidateQueries({ queryKey: TARPS_ROOT });

  const open = useMutation({
    mutationFn: (input: OpenTarpInput) => tarp.openTarp(input),
    onSuccess: invalidate,
  });
  const conclude = useMutation({
    mutationFn: (id: string) => tarp.concludeTarp(id),
    onSuccess: invalidate,
  });
  const assume = useMutation({
    mutationFn: (id: string) => tarp.assumeTarp(id),
    onSuccess: invalidate,
  });
  const close = useMutation({
    mutationFn: (vars: { id: string; input: CloseTarpInput }) => tarp.closeTarp(vars.id, vars.input),
    onSuccess: invalidate,
  });

  return { open, conclude, assume, close };
}
